import classNames from 'classnames';
import { useState } from 'react';
import { hasAsset, versionedAssetUrl } from '@sorrem/engine';
import { useAssetManifest } from '@sorrem-client/ui/hooks/use-asset-manifest';
import { useItemIconBody } from './ItemIconBodyContext';
import styles from './ItemIcon.module.scss';

/** Catalog ids the asset server accepts (`/assets/items/:id`). */
const ITEM_ID_PATTERN = /^[a-z0-9_-]+$/;

interface ItemIconProps {
    /** The item's model id (see `resolveItemModelId`), e.g. `shortsword`. */
    modelId: string;
    /**
     * The body to show the icon for (a `characterModelId`): outfit armor has
     * an icon per body. Defaults to the selected character's body (from
     * `ItemIconBodyProvider`); pass it where the icon belongs to another
     * body, such as a character being created.
     */
    characterModelId?: string;
    /** Accessible name; omit when a label nearby already names the item. */
    alt?: string;
    className?: string;
}

/**
 * The icon file the asset server would send for an item (a body's own icon,
 * else its one icon), at its versioned URL (#285), so a cached icon needs no
 * request; null when the manifest lists neither (the route then decides).
 */
export function resolveVersionedItemIconUrl(modelId: string, body: string | undefined): string | null {
    const candidates = [...(body ? [`item-models/${modelId}/icon-${body}.png`] : []), `item-models/${modelId}/icon.png`];
    const path = candidates.find((candidate) => hasAsset(candidate));
    return path === undefined ? null : versionedAssetUrl(path);
}

/**
 * An item's icon from the asset server, which returns the PNG at
 * `/assets/items/:id` when asked for an image (`?body=` picks a body's own
 * icon when the item has one). Renders nothing for ids the catalog can't
 * hold (crystals, placeholders) or when no icon exists. Once the asset
 * manifest has loaded, the icon file itself is used, versioned, so it comes
 * straight from the cache.
 */
export function ItemIcon({ modelId, characterModelId, alt = '', className }: ItemIconProps): JSX.Element | null {
    const [failedSrc, setFailedSrc] = useState<string | null>(null);
    const defaultBody = useItemIconBody();
    const manifest = useAssetManifest();
    const body = characterModelId ?? defaultBody;
    if (!ITEM_ID_PATTERN.test(modelId)) {
        return null;
    }

    const validBody = body && ITEM_ID_PATTERN.test(body) ? body : undefined;
    const routeSrc = validBody ? `/assets/items/${modelId}?body=${validBody}` : `/assets/items/${modelId}`;
    const src = (manifest !== null ? resolveVersionedItemIconUrl(modelId, validBody) : null) ?? routeSrc;
    if (failedSrc === src) {
        return null;
    }

    return (
        <img
            className={classNames(styles.itemIcon, className)}
            src={src}
            alt={alt}
            draggable={false}
            onError={() => setFailedSrc(src)}
        />
    );
}
